/* One category: default row, or expanded inline editor.

   Inline rather than a modal because renaming is a routine action and §31 asks
   for a quiet UI; only the irreversible delete escalates to a dialog.

   Secondary actions are disclosed per pointer and the two arms are a render
   branch, not a CSS one — the same shape as index-row, so someone who learned
   the interaction there meets the same object here: a fine pointer gets the
   hover cluster, a coarse one gets a ⋮ that opens the workspace's action sheet.

   Emits `save` ({name, description}), `remove`, `edit`, `cancel` and `menu`.
   Purely presentational — the parent owns the API calls. */

import { LitElement, html, css } from 'lit';
import { iconActions, revealOnHover, shadowReset } from '../../design/base.js';
import { settingsRow } from './row.js';
import { customElement, property, state } from 'lit/decorators.js';
import type { Category } from '../../core/types.js';
import '../../component/g-button/g-button.js';
import '../../component/g-field/g-field.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';

@customElement('settings-category-row')
export class SettingsCategoryRow extends LitElement {
  static override styles = [
    shadowReset,
    settingsRow,
    iconActions,
    revealOnHover,
    css`
      :host {
        display: block;
        position: relative;
        /* Width the row reserves at its right edge so a long description never
           runs under the actions. Two icon buttons — (15px icon + 2*4px pad +
           2*1px transparent border) * 2 + 2px gap. The border is easy to forget
           and makes the naive figure 48px. */
        --acts-col: 52px;
      }
      /* One ⋮: a 46px button whose box hangs --space-3 outside --row-pad-r, plus
         --space-2 of clearance, since this row's reservation carries no gap term
         of its own. */
      :host([touch]) {
        --acts-col: 42px;
      }
      /* The one list in this workspace that keeps dividers — numerous, alike and
         scanned, which is the case they earn (row.ts). On the bottom edge,
         matching index-row. */
      :host(:not(:last-child))::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: var(--row-pad-l);
        right: var(--row-pad-r);
        height: 1px;
        background: var(--border);
      }
      /* A category row is two lines that wrap, not a single centred control, so
         it opts out of .row's centring. It is also not a button — there is no
         primary action the whole row could carry. */
      .main {
        align-items: flex-start;
        padding-right: calc(var(--row-pad-r) + var(--acts-col));
      }
      .main.bare {
        padding-right: var(--row-pad-r);
      }
      .body {
        min-width: 0;
      }
      .top {
        display: flex;
        align-items: baseline;
        gap: var(--space-2);
      }
      .name {
        font-weight: 700;
        overflow-wrap: anywhere;
      }
      /* Grouped with the name rather than right-aligned: in a 760px measure a
         right-aligned column left ~600px of void between a short name and its
         number. 0.75rem is the workspace's one mono size (ADR 0011 decision 7). */
      .count {
        color: var(--accent);
        font-family: var(--font-mono);
        font-size: 0.75rem;
        flex: none;
      }
      .desc {
        margin: 1px 0 0;
        color: var(--text-dim);
        font-size: 0.82rem;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }
      .locked {
        font-style: italic;
      }
      /* Centred on the row's first line rather than its middle, so it holds its
         place when a two-line description grows below it. §19's reveal, the hit
         area and the touch sizing all come from iconActions + revealOnHover. */
      .acts {
        position: absolute;
        top: var(--space-2);
        right: calc(var(--row-inset-r) + var(--space-2));
        align-items: center;
        min-height: calc(0.95rem * 1.5);
      }
      /* Offset by the button's own padding so the ICON's edge lands on
         --row-pad-r rather than the button box's — see index-row, which this
         matches deliberately. */
      .trigger {
        display: flex;
        position: absolute;
        top: var(--space-2);
        right: calc(var(--row-pad-r) - var(--space-3));
        align-items: center;
        height: calc(0.95rem * 1.5);
        color: var(--text-dim);
        /* 20px icon + 2*12px pad + 2*1px transparent border = 46px, clear of the
           --touch-min floor. g-button's icon-only default is 8px pad, which
           paints 38px. */
        --icon-size: 20px;
        --g-button-pad: var(--space-3);
      }
      /* The editor is content inside the row, so it takes the row's inset. */
      .edit {
        display: grid;
        gap: var(--space-2);
        padding: var(--space-3) var(--row-pad-r) var(--space-3)
          var(--row-pad-l);
      }
      .editActs {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-2);
      }
    `,
  ];

  @property({ attribute: false }) category!: Category;
  /** Controlled by the parent, so only one row can be open at a time. */
  @property({ type: Boolean, reflect: true }) editing = false;
  /** Which arm to render. Set by the workspace, which reads the pointer once. */
  @property({ type: Boolean, reflect: true }) touch = false;

  @state() private draftName = '';
  @state() private draftDesc = '';

  /** Seed the drafts from the category whenever the editor opens. */
  override willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('editing') && this.editing) {
      this.draftName = this.category.name;
      this.draftDesc = this.category.description;
    }
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  private save(): void {
    const name = this.draftName.trim();
    if (!name) return; // the server would 400; don't bother it
    this.emit('save', { name, description: this.draftDesc.trim() });
  }

  private renderEditor() {
    return html`
      <div class="edit">
        <g-field
          shape="box"
          label="Name"
          .value=${this.draftName}
          .debounce=${0}
          @g-input=${(e: CustomEvent<string>) => (this.draftName = e.detail)}
        ></g-field>
        <!-- Single-line deliberately, not a textarea: this value is rendered onto
             ONE line of the Gemini analysis prompt (analyze._category_rule), so a
             newline typed here used to forge an extra field rule in that prompt.
             categories.one_line is the actual fix, server-side; this stops the UI
             from inviting the input in the first place. -->
        <g-field
          shape="box"
          label="Description"
          placeholder="what belongs in this category…"
          .value=${this.draftDesc}
          .debounce=${0}
          @g-input=${(e: CustomEvent<string>) => (this.draftDesc = e.detail)}
        ></g-field>
        <g-notice>
          The description steers how <strong>new</strong> saves are classified. Renaming
          keeps the ${this.category.n} item${this.category.n === 1 ? '' : 's'} already here.
        </g-notice>
        <div class="editActs">
          <g-button @click=${() => this.emit('cancel')}>Cancel</g-button>
          <g-button variant="primary" @click=${this.save}>Save</g-button>
        </div>
      </div>
    `;
  }

  private renderActions(c: Category) {
    if (this.touch) {
      return html`
        <span class="trigger">
          <g-button icon-only label="Actions for ${c.name}" @click=${() => this.emit('menu')}>
            <g-icon name="more-vertical"></g-icon>
          </g-button>
        </span>
      `;
    }
    return html`
      <span class="acts">
        <g-button icon-only label="Edit ${c.name}" @click=${() => this.emit('edit')}>
          <g-icon name="edit"></g-icon>
        </g-button>
        <g-button
          variant="danger"
          icon-only
          label="Delete ${c.name}"
          @click=${() => this.emit('remove')}
        >
          <g-icon name="trash"></g-icon>
        </g-button>
      </span>
    `;
  }

  override render() {
    const c = this.category;
    if (this.editing) return this.renderEditor();
    return html`
      <div class="row main ${c.protected ? 'bare' : ''}">
        <div class="body">
          <div class="top">
            <span class="name">${c.name}</span>
            <span class="count" title="${c.n} saved item${c.n === 1 ? '' : 's'}">${c.n}</span>
          </div>
          <p class="desc ${c.protected ? 'locked' : ''}">
            ${c.protected
              ? 'Always present — the fallback when nothing else fits. Cannot be renamed or deleted.'
              : c.description || 'No description — add one to steer classification.'}
          </p>
        </div>
      </div>
      ${c.protected ? '' : this.renderActions(c)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'settings-category-row': SettingsCategoryRow;
  }
}
